import { useCallback, useEffect, useMemo, useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { TrendingUp, TrendingDown, Save } from "lucide-react";
import { toast } from "sonner";
import { api, money, shortDate } from "../lib/api";
import UpgradeGate from "../components/UpgradeGate";

export default function DailyPricing({ user, currency, onUpgraded }) {
  const isPro = user.plan === "pro";
  const [products, setProducts] = useState(null);
  const [history, setHistory] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [notes, setNotes] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(() => {
    if (!isPro) return;
    Promise.all([
      api.get("/products").then((r) => { setProducts(r.data); setDrafts(Object.fromEntries(r.data.map((p) => [p.id, p.price]))); }),
      api.get("/price_history").then((r) => setHistory(r.data)),
    ]).catch(() => toast.error("Could not load daily pricing"));
  }, [isPro]);

  useEffect(() => { load(); }, [load]);

  const historyByProduct = useMemo(() => {
    const map = {};
    history.forEach((entry) => {
      map[entry.product_id] = map[entry.product_id] || [];
      map[entry.product_id].push({ date: entry.date, price: entry.new_price, prev: entry.previous_price });
    });
    Object.keys(map).forEach((id) => map[id].reverse());
    return map;
  }, [history]);

  if (!isPro) {
    return (
      <UpgradeGate
        title="Daily pricing is a Pro superpower"
        description="Update your product prices as often as they change and let FlowSphere revalue your inventory automatically."
        benefits={[
          "Update the price for any product in one click",
          "Automatic stock revaluation on every price change",
          "Full price history with movement chart per product",
          "Perfect for commodities, gold, produce, and volatile SKUs",
        ]}
        onUpgraded={onUpgraded}
      />
    );
  }

  if (!products) return <div className="skeleton-grid"><div /><div /><div /></div>;

  const totalValue = products.reduce((sum, p) => sum + (p.price || 0) * (p.stock || 0), 0);

  const saveOne = async (product) => {
    const newPrice = Number(drafts[product.id]);
    if (!newPrice || newPrice <= 0) { toast.error("Enter a valid price"); return; }
    if (Math.abs(newPrice - product.price) < 0.001) { toast.info("Price unchanged"); return; }
    setSavingId(product.id);
    try {
      await api.post(`/products/${product.id}/price`, { price: newPrice, note: notes[product.id] || "" });
      toast.success(`${product.name} → ${money(newPrice, currency)}`);
      setNotes((prev) => ({ ...prev, [product.id]: "" }));
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update price");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="daily-pricing">
      <div className="pricing-summary">
        <div>
          <span>Total inventory value at current prices</span>
          <strong data-testid="pricing-total-value">{money(totalValue, currency)}</strong>
        </div>
        <div>
          <span>Products tracked</span>
          <strong>{products.length}</strong>
        </div>
        <div>
          <span>Price updates logged</span>
          <strong>{history.length}</strong>
        </div>
      </div>
      <section className="panel module-panel">
        <div className="module-toolbar">
          <div><h3>Update daily prices</h3><p>Set today's price for each product. Inventory value updates instantly.</p></div>
        </div>
        <div className="table-wrap">
          <table data-testid="daily-pricing-table">
            <thead>
              <tr>
                <th>Product</th><th>Stock</th><th>Current price</th><th>New price</th><th>Change</th><th>Note</th><th>Value</th><th>7-day trend</th><th></th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const draft = Number(drafts[product.id] || 0);
                const delta = draft - (product.price || 0);
                const value = draft * (product.stock || 0);
                const trend = historyByProduct[product.id] || [];
                return (
                  <tr key={product.id} data-testid={`daily-row-${product.id}`}>
                    <td><strong>{product.name}</strong><small>{product.sku || "—"}</small></td>
                    <td>{product.stock} {product.unit}</td>
                    <td>{money(product.price, currency)}</td>
                    <td>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        data-testid={`daily-price-input-${product.id}`}
                        value={drafts[product.id] ?? ""}
                        onChange={(e) => setDrafts({ ...drafts, [product.id]: e.target.value })}
                        className="inline-input"
                      />
                    </td>
                    <td>
                      {Math.abs(delta) < 0.001 ? (
                        <span className="status draft">No change</span>
                      ) : (
                        <span className={delta > 0 ? "delta-up" : "delta-down"}>
                          {delta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {money(Math.abs(delta), currency)}
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        placeholder="e.g. market rate"
                        data-testid={`daily-note-input-${product.id}`}
                        value={notes[product.id] || ""}
                        onChange={(e) => setNotes({ ...notes, [product.id]: e.target.value })}
                        className="inline-input"
                      />
                    </td>
                    <td><strong>{money(value, currency)}</strong></td>
                    <td className="trend-cell">
                      {trend.length > 1 ? (
                        <div style={{ width: 100, height: 32 }}>
                          <ResponsiveContainer initialDimension={{ width: 100, height: 32 }}>
                            <LineChart data={trend}>
                              <XAxis dataKey="date" hide />
                              <Tooltip formatter={(value) => money(value, currency)} labelFormatter={(label) => shortDate(label)} />
                              <Line type="monotone" dataKey="price" stroke="#2FB6E4" strokeWidth={2} dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <span className="mini-empty" style={{ padding: 0, fontSize: 10 }}>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="primary-button"
                        data-testid={`daily-save-${product.id}`}
                        onClick={() => saveOne(product)}
                        disabled={savingId === product.id || Math.abs(delta) < 0.001}
                      >
                        <Save size={13} /> {savingId === product.id ? "Saving…" : "Update"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div><h3>Recent price movements</h3><p>Every update is logged with the previous and new price.</p></div>
        </div>
        {history.length === 0 ? (
          <div className="mini-empty">No price changes yet. Save a new price above to start your history.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Product</th><th>Previous</th><th>New</th><th>Change</th><th>Value at change</th><th>Note</th></tr></thead>
              <tbody>
                {history.slice(0, 40).map((entry) => (
                  <tr key={entry.id}>
                    <td>{shortDate(entry.created_at)}</td>
                    <td><strong>{entry.product_name}</strong></td>
                    <td>{money(entry.previous_price, currency)}</td>
                    <td><strong>{money(entry.new_price, currency)}</strong></td>
                    <td className={entry.delta > 0 ? "delta-up" : "delta-down"}>{entry.delta > 0 ? "+" : ""}{money(entry.delta, currency)}</td>
                    <td><strong>{money(entry.value_at_change, currency)}</strong></td>
                    <td>{entry.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
